import _styled from "@emotion/styled-base";
import { SortOrder, ColumnMenu } from '@risk-first/ui-column-menu';
import React, { useCallback, useRef } from 'react';
import { TickWhiteIcon } from '@risk-first/ui-assets';
import { themeGet } from '@styled-system/theme-get';
import { AgGridReact } from 'ag-grid-react';
import { withTheme } from 'emotion-theming';
import { rem } from 'polished';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-balham.css';
import { Loading } from '@risk-first/ui-loading';

const Header = props => {
  const {
    enableSorting,
    setSort
  } = props;
  const colDef = props.column.getColDef();
  const filters = (colDef.headerComponentParams || {}).columnFilters;
  const handleApplyFilters = (colDef.headerComponentParams || {}).handleApplyFilters;
  const handleSort = useCallback(order => {
    setSort(order === SortOrder.ASC ? 'asc' : 'desc');
  }, [setSort]);
  return React.createElement(ColumnMenu, {
    enableSorting: enableSorting,
    sortFunction: handleSort,
    title: props.displayName,
    filters: filters,
    onApplyFilters: handleApplyFilters
  });
};

var ResizeStrategy;

(function (ResizeStrategy) {
  ResizeStrategy[ResizeStrategy["AutoSizeColumns"] = 0] = "AutoSizeColumns";
  ResizeStrategy[ResizeStrategy["None"] = 1] = "None";
  ResizeStrategy[ResizeStrategy["SizeColumnsToFit"] = 2] = "SizeColumnsToFit";
})(ResizeStrategy || (ResizeStrategy = {}));

const DefaultCustomLoadingOverlay = () => React.createElement(Loading, null);

const getRowNodeIdFromEvent = (event, getRowNodeId) => getRowNodeId ? getRowNodeId(event.data) : undefined;

const StyledDiv = withTheme(_styled("div", {
  target: "e1gngbo50",
  label: "StyledDiv"
})("font-family:", themeGet('tableDataFont'), ";font-size:", rem(14), ";height:100%;width:100%;.ag-header{border-top:0;border-bottom:0;}.ag-header-cell{background:", themeGet('colors.white'), ";font-family:", themeGet('tableHeaderFont'), ";.ag-header-cell-label{display:flex;align-items:center;color:", themeGet('colors.tableHeader'), ";font-weight:500;letter-spacing:0.23px;line-height:1;}&::after{background:", themeGet('colors.white'), ";}}.ag-header-row{border-bottom:1px solid ", themeGet('colors.border'), ";}.ag-root-wrapper{border:0;}.ag-row{color:", themeGet('colors.text'), ";line-height:2.86;opacity:", themeGet('tableDataOpacity'), ";.ag-cell{display:flex;align-items:center;}border-top:0;border-bottom:0;}.ag-row-hover{background:none;}.ag-row-selected{background:", themeGet('colors.white'), ";}.ag-row-even{background:", themeGet('colors.ghostWhite'), ";.ag-row-selected{background:inherit;}}.ag-checkbox-input-wrapper{background:white;&:focus-within{box-shadow:none;}&:after{color:", themeGet('colors.border'), ";}&.ag-checked::after{background:", themeGet('colors.pink'), ";border-radius:3px;content:url('", TickWhiteIcon, "');}}"));
const Table = React.memo(props => {
  const {
    agGridProps,
    columns,
    getRowNodeId,
    headerHeight = 40,
    onRowClicked,
    onRowDoubleClicked,
    onRowSelected,
    resizeStrategy = ResizeStrategy.SizeColumnsToFit,
    rowHeight = 40,
    tabToNextRow = true,
    onGridReady
  } = props;
  const gridApi = useRef();
  const resize = useCallback(params => {
    if (resizeStrategy === ResizeStrategy.AutoSizeColumns) {
      params.columnApi.autoSizeAllColumns();
    } else if (resizeStrategy === ResizeStrategy.SizeColumnsToFit) {
      params.api.sizeColumnsToFit();
    }
  }, [resizeStrategy]);
  const onRowClickedProxy = useCallback(event => {
    if (onRowClicked) {
      onRowClicked({
        id: getRowNodeIdFromEvent(event, getRowNodeId),
        originalEvent: event
      });
    }
  }, [getRowNodeId, onRowClicked]);
  const onRowSelectedProxy = useCallback(event => {
    if (onRowSelected) {
      onRowSelected({
        id: getRowNodeIdFromEvent(event, getRowNodeId),
        isSelected: event.node.isSelected(),
        originalEvent: event
      });
    }
  }, [getRowNodeId, onRowSelected]);
  const onRowDoubleClickedProxy = useCallback(event => {
    if (onRowDoubleClicked) {
      onRowDoubleClicked({
        id: getRowNodeIdFromEvent(event, getRowNodeId),
        originalEvent: event
      });
    }
  }, [getRowNodeId, onRowDoubleClicked]);
  const handleTabToNextRow = useCallback(params => {
    var _a;

    const renderedRowCount = ((_a = gridApi.current) === null || _a === void 0 ? void 0 : _a.getLastDisplayedRow()) || 0;
    const previousCellPosition = params.previousCellPosition;
    const previousRowIndex = previousCellPosition.rowIndex;
    let nextRowIndex = params.backwards ? previousRowIndex - 1 : previousRowIndex + 1;

    if (nextRowIndex < 0) {
      nextRowIndex = 0;
    }

    if (nextRowIndex >= renderedRowCount) {
      nextRowIndex = renderedRowCount;
    }

    const result = {
      rowIndex: nextRowIndex,
      column: previousCellPosition.column,
      rowPinned: previousCellPosition.rowPinned
    };
    return result;
  }, []);

  const onGridReadyProxy = params => {
    gridApi.current = params.api;
    if (onGridReady) onGridReady(params);
  };

  return React.createElement(StyledDiv, {
    className: "ag-theme-balham"
  }, React.createElement(AgGridReact, Object.assign({}, props, {
    columnDefs: columns,
    onFirstDataRendered: resize,
    onGridColumnsChanged: resize,
    rowHeight: rowHeight,
    headerHeight: headerHeight,
    suppressPropertyNamesCheck: true,
    onRowClicked: onRowClickedProxy,
    onRowSelected: onRowSelectedProxy,
    onRowDoubleClicked: onRowDoubleClickedProxy,
    tabToNextCell: tabToNextRow ? handleTabToNextRow : undefined,
    onGridReady: onGridReadyProxy,
    defaultColDef: (agGridProps === null || agGridProps === void 0 ? void 0 : agGridProps.defaultColDef) ? {
      headerComponentFramework: agGridProps.defaultColDef.headerComponentFramework
    } : {
      headerComponentFramework: Header
    },
    gridOptions: agGridProps === null || agGridProps === void 0 ? void 0 : agGridProps.gridOptions,
    frameworkComponents: Object.assign({
      customLoadingOverlay: DefaultCustomLoadingOverlay
    }, agGridProps === null || agGridProps === void 0 ? void 0 : agGridProps.frameworkComponents),
    loadingOverlayComponent: "customLoadingOverlay"
  })));
});
export { ResizeStrategy, Table };
//# sourceMappingURL=index.js.map
